import type { AdultGigRequestListItemResponse, GigListItemResponse } from "../../../types/gigTypes";
import { formatDateTimeInGigTimeZone, formatWeekdayInTimeZone } from "../../../utils/DateUtils";
import { formatGigScheduleSummary } from "../../../utils/GigScheduleUtils";

const toWeekdayLabel = (value: string | null, timeZone?: string | null): string | null =>
  formatWeekdayInTimeZone(value, timeZone);

export const formatDateRange = (startTime: string | null, endTime: string | null, timeZone?: string | null): string => {
  if (!startTime && !endTime) {
    return "No proposed time yet";
  }

  if (startTime && endTime) {
    return `${formatDateTimeInGigTimeZone(startTime, timeZone)} to ${formatDateTimeInGigTimeZone(endTime, timeZone)}`;
  }

  return formatDateTimeInGigTimeZone(startTime ?? endTime, timeZone);
};

export const formatWeekdayRange = (startTime: string | null, endTime: string | null, timeZone?: string | null): string | null => {
  const startLabel = toWeekdayLabel(startTime, timeZone);
  const endLabel = toWeekdayLabel(endTime, timeZone);

  if (!startLabel && !endLabel) {
    return null;
  }

  if (startLabel && endLabel) {
    return startLabel === endLabel ? startLabel : `${startLabel} to ${endLabel}`;
  }

  return startLabel ?? endLabel;
};

export const formatDateRangeWithWeekdays = (startTime: string | null, endTime: string | null, timeZone?: string | null): string => {
  const dateRange = formatDateRange(startTime, endTime, timeZone);
  const startLabel = toWeekdayLabel(startTime, timeZone);
  const endLabel = toWeekdayLabel(endTime, timeZone);

  if (startTime && endTime && startLabel && endLabel) {
    return `${startLabel}, ${formatDateTimeInGigTimeZone(startTime, timeZone)} to ${endLabel}, ${formatDateTimeInGigTimeZone(endTime, timeZone)}`;
  }

  if (startLabel && startTime) {
    return `${startLabel}, ${formatDateTimeInGigTimeZone(startTime, timeZone)}`;
  }

  if (endLabel && endTime) {
    return `${endLabel}, ${formatDateTimeInGigTimeZone(endTime, timeZone)}`;
  }

  return dateRange;
};

export const formatGigSchedule = (gig: GigListItemResponse): string => {
  const summary = formatGigScheduleSummary(gig);
  if (summary !== "Schedule to be confirmed") {
    return summary;
  }
  if (gig.startTime && gig.endTime) {
    return `${formatDateTimeInGigTimeZone(gig.startTime, gig.timeZone)} to ${formatDateTimeInGigTimeZone(gig.endTime, gig.timeZone)}`;
  }
  if (gig.startTime || gig.endTime) {
    return formatDateTimeInGigTimeZone(gig.startTime ?? gig.endTime, gig.timeZone);
  }
  if (gig.estimatedDurationMinutes) {
    return `${gig.estimatedDurationMinutes} min estimated duration`;
  }
  return summary;
};

export const getTeenName = (request: AdultGigRequestListItemResponse): string => {
  return [request.teenFirstName, request.teenLastName].filter(Boolean).join(" ") || "Teen applicant";
};

export const formatTeenAge = (dateOfBirth: string | null): string => {
  if (!dateOfBirth) {
    return "Unknown";
  }

  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) {
    return "Unknown";
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return `${age}`;
};
